import { inject, Injectable } from '@angular/core';
import { AuthFacadeService } from '../../domain/auth/service/auth-facade.service';
import { ApplicationConfig } from '../config/application.config';

export const SESSION_TIMEOUT_MS = 60 * 60 * 1000;

/** Last activity in any tab; outside the `auth.` keys, so the tab sync ignores it. */
export const LAST_ACTIVITY_KEY = `${ApplicationConfig.applicationName}:session.lastActivity`;

/**
 * Signs out after SESSION_TIMEOUT_MS without activity in any open tab.
 * Each tab records its activity in localStorage; when a tab's timer fires it
 * first checks the shared last activity, so working in one tab keeps every
 * other tab (and the session they share) alive.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly authFacade = inject(AuthFacadeService);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  start(): void {
    this.reset();
  }

  reset(): void {
    this.writeLastActivity(Date.now());
    this.schedule(SESSION_TIMEOUT_MS);
  }

  stop(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private schedule(delayMs: number): void {
    this.stop();
    this.timeoutId = setTimeout(() => this.onTimeout(), delayMs);
  }

  private onTimeout(): void {
    this.timeoutId = null;
    const idleMs = Date.now() - this.readLastActivity();
    if (idleMs < SESSION_TIMEOUT_MS) {
      // Someone was active in another tab: wait for the rest of their window.
      this.schedule(SESSION_TIMEOUT_MS - idleMs);
      return;
    }
    this.authFacade.sessionTimeout();
  }

  private readLastActivity(): number {
    try {
      return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private writeLastActivity(at: number): void {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(at));
    } catch {
      // Without storage each tab keeps its own timer, as before.
    }
  }
}
